import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useMemo } from 'react';
import { countries } from '../../../../../constants';
import { getAcademicTitle } from '../../../../../Interfaces/IAcademicTitle';
import { staticArrayAsProxy } from '../../../../../Interfaces/IAsProxy';
import {
  staticContractObjectSubtypeArray,
  staticContractObjectTypeArray,
} from '../../../../../Interfaces/IContractObjectType';
import { IConfig } from '../../../../../Interfaces/IConfig';
import { ILegalTransactionRelationship } from '../../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import { ISignatureCertificationRelationshipData } from '../../../../../Interfaces/ILegalTransactionRelationship/Relationships/SignatureCertification/ILegalTransactionRelationshipSignatureCertificationData';
import { staticTypeOfIdentificationArray } from '../../../../../Interfaces/ITypeOfIdentification';
import { checkString } from '../../../../core/standardFunctions';
import { CustomeTextField } from '../../../../generic/CustomeTextField';
import { SendLegalClient } from '../../../../LegalClient/SendLegalClient';
import {
  getOrCreateSignatureCertificationRelationship,
  isSignatureCertificationMember,
} from '../utilities';
import { DialogLegalTransactionTypeMapping } from '../../../../../Interfaces/IDialogLegalTransactionTypeMapping';
import { useLegalClientStore } from '../../generic/legalClientStore';

interface ISignatureCertificationSummaryProps {
  configObject: IConfig;
  signatureCertificationData: ISignatureCertificationRelationshipData;
  setSignatureCertificationData: React.Dispatch<
    React.SetStateAction<ISignatureCertificationRelationshipData>
  >;
  relationshipArray: ILegalTransactionRelationship[];
  setRelationshipArray: React.Dispatch<
    React.SetStateAction<ILegalTransactionRelationship[]>
  >;
  handleNext: () => void;
  handleBack: () => void;
}

export const SignatureCertificationSummary: React.FC<
  ISignatureCertificationSummaryProps
> = ({
  configObject,
  signatureCertificationData: scObject,
  setSignatureCertificationData: setScObject,
  relationshipArray,
  setRelationshipArray,
  handleNext,
  handleBack,
}) => {
  const getLegalClient = useLegalClientStore(state => state.getLegalClient);

  const signatureCertificationRelationship = useMemo(
    () => getOrCreateSignatureCertificationRelationship(relationshipArray),
    [relationshipArray]
  );
  const members = useMemo(
    () =>
      signatureCertificationRelationship.Members.filter(
        isSignatureCertificationMember
      ),
    [signatureCertificationRelationship]
  );

  return (
    <>
      <Typography variant='h4' sx={{ mb: 3 }}>
        Abschluss
      </Typography>

      <Typography>
        Vielen Dank für die Eingabe Ihrer Daten. Bitte überprüfen Sie Ihre Daten
        nun noch einmal in der Übersicht. Wenn alles korrekt ist, senden Sie
        Ihre Daten bitte ab.
      </Typography>

      <Box sx={{ mt: 5 }} />
      <Typography variant='h6' sx={{ mb: 3 }}>
        Abschließende Angaben zur Unterschriftsbeglaubigung
      </Typography>
      <CustomeTextField
        attr='notes'
        label='Anmerkungen'
        object={scObject}
        setObject={setScObject}
        type='string'
        rows={5}
      />
      <Box sx={{ mt: 5 }} />
      <Typography variant='h6' sx={{ mb: 3 }}>
        Zusammenfassung
      </Typography>

      <Typography variant='h6' sx={{ mb: 3 }}>
        Beteiligte
      </Typography>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell width={20}>Nr.</TableCell>
            <TableCell width={120}>Titel</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Ausweisdokument</TableCell>
            <TableCell width={100}>Als Vertretung</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {members
            .map(member => ({
              ...member,
              LegalClient: getLegalClient(member.idLegalClient),
            }))
            .map(
              ({ LegalClient: legalClient, data }, idx) =>
                legalClient && (
                  <TableRow>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>
                      {checkString(
                        getAcademicTitle(legalClient.idAcademicTitle)
                      )}
                    </TableCell>
                    <TableCell>
                      {legalClient.FirstName} {legalClient.LastName}
                    </TableCell>
                    <TableCell>
                      {
                        staticTypeOfIdentificationArray.find(
                          x => x.idTypeOfDocument === data.idTypeOfDocument
                        )?.TypeOfDocument
                      }
                    </TableCell>
                    <TableCell>
                      {!data.asProxy
                        ? 'Nein'
                        : staticArrayAsProxy.find(
                            x => x.idAsProxy === data.idAsProxy
                          )?.typeAsProxy}
                    </TableCell>
                  </TableRow>
                )
            )}
        </TableBody>
      </Table>

      <Box sx={{ mt: 10 }} />
      <Typography variant='h6' sx={{ mb: 3 }}>
        Gegenstand
      </Typography>
      <Grid container sx={{ pl: 10 }} spacing={2}>
        <Grid item sm={4}>
          <b>Vertragsart:</b>
        </Grid>
        <Grid item sm={8}>
          {
            staticContractObjectTypeArray.find(
              x => x.idContractObjectType === scObject.idContractObjectType
            )?.contractObjectType
          }
        </Grid>
        <Grid item sm={4}>
          <b>Vertragsinhalt:</b>
        </Grid>
        <Grid item sm={8}>
          {
            staticContractObjectSubtypeArray.find(
              x => x.idContactObjectSubtype === scObject.idContactObjectSubtype
            )?.contactObjectSubtype
          }
        </Grid>

        {staticContractObjectSubtypeArray.find(
          x => x.idContactObjectSubtype === scObject.idContactObjectSubtype
        )?.allowFreeText && (
          <>
            <Grid item sm={4} />
            <Grid item sm={8}>
              {checkString(scObject.textcontactObjectSubtype)}
            </Grid>
          </>
        )}
      </Grid>

      <Box sx={{ mt: 10 }} />
      <Typography variant='h6' sx={{ mb: 3 }}>
        Verwendung
      </Typography>
      <Grid container sx={{ pl: 10 }} spacing={2}>
        <Grid item sm={4}>
          <b>Sprache:</b>
        </Grid>
        <Grid item sm={8}>
          {
            countries.find(x => x.idCountry === scObject.idCountryTextLanguage)
              ?.Nationality
          }
        </Grid>
        <Grid item sm={4}>
          <b>Verwendung:</b>
        </Grid>
        <Grid item sm={8}>
          {
            countries.find(x => x.idCountry === scObject.idCountryUseOfText)
              ?.Country
          }
        </Grid>
      </Grid>

      <SendLegalClient
        configObject={configObject}
        idLegalTransactionType={
          DialogLegalTransactionTypeMapping.SignatureCertification
        }
        relationshipArray={relationshipArray}
        setRelationshipArray={setRelationshipArray}
        handleBack={handleBack}
        handleNext={handleNext}
      />
    </>
  );
};
